import { createAsyncThunk } from '@reduxjs/toolkit'
import type { AxiosError } from 'axios'

import { ValidationErrors } from 'store/types'

import { getAxiosInstance } from 'services/api'

import { Game, RequestGame } from 'types/app'

import { GameParams } from './types'

const api = getAxiosInstance()

export const getAllGames = createAsyncThunk<
  Game[],
  void,
  { rejectValue: ValidationErrors }
>('game/getAllGames', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/all/games')
    return response.data.games
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const createGame = createAsyncThunk<
  Game,
  GameParams,
  { rejectValue: ValidationErrors }
>(
  'game/create',
  async (
    { avatar, cover_photo, name, url, description },
    { rejectWithValue },
  ) => {
    try {
      const params = new FormData()
      if (avatar) params.append('avatar', avatar)
      if (name) params.append('name', name)
      if (url) params.append('url', url)
      if (cover_photo) params.append('cover_photo', cover_photo)
      if (description) params.append('description', description)

      const response = await api.post('/create/game', params, {
        headers: {
          'content-type': 'multipart/form-data',
        },
      })
      return response.data.game
    } catch (err) {
      const error = err as AxiosError<ValidationErrors>

      if (!error.response) throw err

      return rejectWithValue(error.response.data)
    }
  },
)

export const followGame = createAsyncThunk<
  Game,
  string,
  { rejectValue: ValidationErrors }
>('game/follow', async (gameId, { rejectWithValue }) => {
  console.log('hit followGame')

  console.log('gameId', gameId)

  try {
    const response = await api.put(`/game/follow/${gameId}`)
    return response.data.game
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const requestToBeOwner = createAsyncThunk<
  Game,
  string,
  { rejectValue: ValidationErrors }
>('game/requestToBeOwner', async (gameId, { rejectWithValue }) => {
  try {
    const response = await api.post(`/game/request/${gameId}`)
    return response.data.game
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const getAllRequestedGame = createAsyncThunk<
  RequestGame[],
  { gameId: string },
  { rejectValue: ValidationErrors }
>('game/getAllRequestedGame', async ({ gameId }, { rejectWithValue }) => {
  try {
    const response = await api.get(`admin/requested/games/${gameId}`)
    // console.log('Response data:', response.data) // Log the response data for debugging
    return response.data.requestsWithUsersAndGames
  } catch (err) {
    // console.error('Error occurred:', err) // Log the error for debugging
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const approveOrRejectRequest = createAsyncThunk<
  { game: Game; requests: RequestGame[]; message: string },
  { id: string; status: string },
  { rejectValue: ValidationErrors }
>(
  'game/approveOrRejectRequest',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/admin/requested/game/${id}`, { status })
      return response.data
    } catch (err) {
      const error = err as AxiosError<ValidationErrors>

      if (!error.response) throw err

      return rejectWithValue(error.response.data)
    }
  },
)

export const cancelOwnerShip = createAsyncThunk<
  { game: Game; request: RequestGame; message: string },
  { id: string },
  { rejectValue: ValidationErrors }
>('game/cancelOwnerShip', async ({ id }, { rejectWithValue }) => {
  try {
    const response = await api.put(`/admin/cancel/ownership/${id}`)
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const verifyGame = createAsyncThunk<
  { game: Game; message: string },
  string,
  { rejectValue: ValidationErrors }
>('game/verifyGame', async (gameId, { rejectWithValue }) => {
  try {
    const response = await api.put(`/admin/game/${gameId}/verify`)
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

// router.route("/game/details/:id").get(getGameDetails);
export const getGameDetails = createAsyncThunk<
  Game,
  string,
  { rejectValue: ValidationErrors }
>('game/getGameDetails', async (gameId, { rejectWithValue }) => {
  try {
    const response = await api.get(`/game/details/${gameId}`)
    return response.data.game
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const updateGame = createAsyncThunk<
  { game: Game; message: string },
  { gameId: string; game: any },
  { rejectValue: ValidationErrors }
>('game/updateGame', async ({ gameId, game }, { rejectWithValue }) => {
  try {
    const params = new FormData()
    console.log('game in action', game.avatar)

    if (game.avatar) params.append('avatar', game.avatar)
    if (game.name) params.append('name', game.name)
    if (game.url) params.append('url', game.url)
    if (game.cover_photo) params.append('cover_photo', game.cover_photo)
    if (game.description) params.append('description', game.description)

    const response = await api.put(`/game/update/${gameId}`, params, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    })
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    if (!error.response) throw err
    return rejectWithValue(error.response.data)
  }
})

export const submitGame = createAsyncThunk<
  { success: boolean; msg: string; game: Game },
  { name: string; description?: string; url?: string; platform?: string; avatar?: File },
  { rejectValue: ValidationErrors }
>('game/submitGame', async (payload, { rejectWithValue }) => {
  try {
    const params = new FormData()
    params.append('name', payload.name)
    if (payload.description) params.append('description', payload.description)
    if (payload.url) params.append('url', payload.url)
    if (payload.platform) params.append('platform', payload.platform)
    if (payload.avatar) params.append('avatar', payload.avatar)

    const response = await api.post('/game/submit', params, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    })
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    if (!error.response) throw err
    return rejectWithValue(error.response.data)
  }
})

export const deleteGameByAdmin = createAsyncThunk<
  { game: Game; message: string },
  string,
  { rejectValue: ValidationErrors }
>('game/deleteGameByAdmin', async (gameId, { rejectWithValue }) => {
  try {
    const response = await api.delete(`/admin/game/${gameId}`)
    return {
      game: response.data.game,
      message: response.data.message,
      gameId,
    }
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    if (!error.response) throw err
    return rejectWithValue(error.response.data)
  }
})
